import { Link } from "react-router-dom";
import { Phone, ShieldCheck, Clock, Wrench, Users, ArrowRight, MapPin } from "lucide-react";
import { SITE, GALLERY } from "@/data/site";
import { getGeneralFaqs } from "@/data/faqs";
import { useLang } from "@/lib/i18n";
import FAQ from "@/components/site/FAQ";
import ReadyCTA from "@/components/site/ReadyCTA";

export default function About() {
  const { t, lang } = useLang();
  const ICONS = [Clock, Wrench, ShieldCheck, Users];
  const FAQS = getGeneralFaqs(lang);

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-navy-dark text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={SITE.teamImage}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            style={{ filter: "blur(3px) brightness(0.72) saturate(1.1)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-navy-dark/78 via-brand-navy-dark/62 to-slate-900/78" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,191,0,0.12)_0%,transparent_55%)]" />
        </div>
        <div className="absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full bg-brand-yellow/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-brand-navy-light/25 blur-3xl pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/60 to-transparent" />
        <div className="relative container-max py-16 sm:py-24">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-yellow text-slate-900 px-3.5 py-1.5 text-xs font-bold tracking-wide shadow-lg shadow-yellow-300/20">
            <Users size={14} />
            {t.about.heroEyebrow}
          </div>
          <h1
            className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.45)" }}
          >
            {t.about.heroTitle}
          </h1>
          <p className="mt-5 text-white/90 text-lg max-w-2xl leading-relaxed" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.4)" }}>
            {t.about.heroSub}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              data-testid="about-call-cta"
              href={`tel:${SITE.primaryPhone}`}
              className="inline-flex items-center justify-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-slate-900 font-bold py-3.5 px-5 rounded-xl whitespace-nowrap shadow-lg shadow-yellow-300/30"
            >
              <Phone size={18} />
              <span>{t.cta.call} {SITE.primaryPhone}</span>
            </a>
            <Link
              data-testid="about-quote-cta"
              to="/contact"
              className="inline-flex items-center justify-center bg-white/15 backdrop-blur-md border-2 border-white/40 hover:bg-white hover:text-slate-900 text-white font-bold py-3.5 px-5 rounded-xl transition-colors whitespace-nowrap"
            >
              {t.cta.freeEstimate}
            </Link>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-brand-yellow border-y-2 border-yellow-500">
        <div className="container-max py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-slate-900">
          {[
            { v: "12", l: t.about.statYears },
            { v: "4", l: t.about.statCounties },
            { v: "24/7", l: t.about.stat247 },
            { v: `${SITE.reviewCount}+`, l: t.about.statReviews },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <p className="font-heading text-4xl font-bold leading-none">{s.v}</p>
              <p className="text-xs uppercase tracking-wider mt-2 font-semibold">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Meet the team — featured team photo */}
      <section data-testid="about-team" className="section-padding bg-white">
        <div className="container-max grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-200">
              <img
                src={SITE.teamImage}
                alt={lang === "es" ? "Equipo de Faster Plumbing" : "The Faster Plumbing crew"}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-brand-navy text-white rounded-2xl px-5 py-4 shadow-xl hidden sm:block">
              <p className="font-heading text-sm font-bold uppercase tracking-wider text-brand-yellow">
                {t.common.brandTagline}
              </p>
              <p className="text-xs text-white/80 mt-1">
                {lang === "es" ? "Empresa familiar" : "Family-run"} · Lake Worth Beach
              </p>
            </div>
            <div className="absolute -top-4 -right-4 bg-brand-yellow text-slate-900 rounded-2xl px-4 py-3 shadow-xl hidden sm:block">
              <p className="font-heading text-2xl font-bold leading-none">12</p>
              <p className="text-[10px] uppercase tracking-wider mt-1 font-semibold">{t.common.yrs}</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
              {t.about.teamEyebrow}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 leading-tight">
              {t.about.teamTitle}
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">{t.about.teamSub}</p>
            <div className="mt-7 grid grid-cols-2 gap-3 max-w-md">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3">
                <p className="font-heading text-2xl font-bold text-brand-navy leading-none">
                  {SITE.reviewCount}+
                </p>
                <p className="text-xs text-slate-600 mt-1.5 font-medium">{t.about.statReviews}</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3">
                <p className="font-heading text-2xl font-bold text-brand-navy leading-none">4</p>
                <p className="text-xs text-slate-600 mt-1.5 font-medium">{t.about.statCounties}</p>
              </div>
            </div>
            <div className="mt-7">
              <a
                href={`tel:${SITE.primaryPhone}`}
                className="inline-flex items-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-slate-900 font-bold py-3 px-5 rounded-xl whitespace-nowrap"
              >
                <Phone size={17} />
                <span>{SITE.primaryPhone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Our promise */}
      <section className="section-padding bg-[radial-gradient(ellipse_at_top,#EFF6FF_0%,#F8FAFC_55%,#F1F5F9_100%)]">
        <div className="container-max grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="aspect-[5/4] rounded-3xl overflow-hidden border-4 border-white shadow-xl ring-1 ring-slate-200">
              <img src={SITE.trailerImage} alt={lang === "es" ? "Tráiler de Faster Plumbing" : "Faster Plumbing service trailer"} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-4 -right-4 bg-brand-yellow text-slate-900 rounded-2xl px-4 py-3 shadow-xl hidden sm:block">
              <p className="font-heading text-sm font-bold uppercase tracking-wider">
                {lang === "es" ? "Listos para salir" : "Truck stocked"}
              </p>
              <p className="text-xs text-slate-700 mt-0.5">
                {lang === "es" ? "Equipados para el Sur de FL" : "Built for South Florida"}
              </p>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
              {t.about.promiseEyebrow}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2 leading-tight">
              {t.about.promiseTitle}
            </h2>
            <p className="mt-5 text-slate-700 text-lg leading-relaxed">{t.about.promise1}</p>
            <p className="mt-4 text-slate-700 text-lg leading-relaxed">{t.about.promise2}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={`tel:${SITE.primaryPhone}`}
                className="inline-flex items-center justify-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-slate-900 font-bold py-3 px-5 rounded-xl whitespace-nowrap"
              >
                <Phone size={17} />
                <span>{SITE.primaryPhone}</span>
              </a>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-1.5 border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-bold py-3 px-5 rounded-xl transition-colors"
              >
                {t.about.browseServices} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What Faster means */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
              {t.about.fasterEyebrow}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 leading-tight">
              {t.about.fasterTitle}
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {t.about.cards.map((c, i) => {
              const Icon = ICONS[i] || ShieldCheck;
              return (
                <div
                  key={c.t}
                  className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow relative overflow-hidden"
                >
                  <span className="absolute top-0 left-0 right-0 h-1 bg-brand-yellow" />
                  <div className="w-12 h-12 rounded-xl bg-brand-yellow/30 text-brand-yellow-dark flex items-center justify-center">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{c.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* From the field */}
      <section className="section-padding bg-slate-50">
        <div className="container-max">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
              {t.about.fieldEyebrow}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2 leading-tight">
              {t.about.fieldTitle}
            </h2>
            <p className="text-slate-700 mt-2 max-w-2xl">{t.about.fieldSub}</p>
          </div>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {GALLERY.map((g, i) => (
              <div
                key={i}
                className="relative aspect-square rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-lg transition-shadow bg-white"
              >
                <img src={g.url} alt={g.alt} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-navy-dark/90 via-brand-navy-dark/30 to-transparent p-3">
                  <p className="text-[11px] font-semibold text-white leading-tight">{g.caption}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 inline-flex items-center gap-2 bg-white border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
            <MapPin size={16} className="text-brand-yellow-dark" />
            <p className="text-sm text-slate-700">
              <span className="font-semibold text-slate-900">{SITE.address}</span>
            </p>
          </div>
        </div>
      </section>

      <FAQ items={FAQS} eyebrow={lang === "es" ? "Sobre nosotros · FAQ" : "About us · FAQ"} />

      <ReadyCTA />
    </>
  );
}
